import { Button, Typography, Box, darken, Divider, IconButton, TextField, Slider, Popover, Tooltip } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import React, { useState, useEffect } from 'react';
import SportsBasketballRoundedIcon from '@mui/icons-material/SportsBasketballRounded';
import { useTranslation } from 'react-i18next';
import { Player, Line, Frame } from './DialogPlayBuilder';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import PolylineRoundedIcon from '@mui/icons-material/PolylineRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ConeIcon from './ConeIcon.tsx';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CropSquareRoundedIcon from '@mui/icons-material/CropSquareRounded';

export interface ActionButtonsProps {
    selectedPlayer: Player | null;
    createLine: (type: 'straight' | 'zigzag' | 'dashed' | 'bar' | 'shot' | 'handoff') => void;
    handleCreateNewFrame: () => void;
    setIsModalOpen: (isOpen: boolean) => void;
    handleDeleteLine: () => void;
    handleToggleBall: () => void;
    handleSavePlay: () => void;
    hasConnectedLines: boolean;
    handleColorChange: (color: string) => void;
    handleHandoffColorChange: (color: string) => void;
    colorOptions: { value: string }[];
    selectedLine?: Line | null;
    toggleShape: () => void;
    isDoubleBezier: boolean;
    selectedLineId: number | null;
    handleAddOffensivePlayer: (num: number) => void;
    handleAddDefender: (num: number) => void;
    handleAddSpecialDefender: (num: number) => void;
    handleDeletePlayer: () => void;
    currentFrame: Frame;
    selectedPlayerId: number | null;
    handleAddCone: (color: string) => void;
    handleDeleteCone: () => void;
    selectedConeId: number | null;
    selectedConeColor: string;
    handleSetSelectedConeColor: (color: string) => void;
    handleAddTextBox: () => void;
    selectedTextBoxId: number | null;
    selectedTextBoxFontSize: number;
    handleSetSelectedTextBoxFontSize: (fontSize: number) => void;
    handleDeleteSelectedTextBox: () => void;
    selectedShadeId: number | null;
    selectedShadeColor: string;
    handleSetSelectedShadeColor: (color: string) => void;
    handleAddShadeCircle: () => void;
    handleAddShadeRect: () => void;
    handleDeleteSelectedShade: () => void;
    handlePlayerColorChange: (color: string) => void;
    handlePlayerNumberChange: (newNumber: string) => void;
    portrait?: boolean;
}

export default function PlayBuilderActionButtons({
    selectedPlayer,
    createLine,
    handleDeleteLine,
    handleToggleBall,
    handleColorChange,
    handleHandoffColorChange,
    colorOptions,
    selectedLine,
    toggleShape,
    isDoubleBezier,
    selectedLineId,
    handleAddOffensivePlayer,
    handleAddDefender,
    handleAddSpecialDefender,
    handleDeletePlayer,
    selectedPlayerId,
    handleAddCone,
    handleDeleteCone,
    selectedConeId,
    selectedConeColor,
    handleSetSelectedConeColor,
    handleAddTextBox,
    selectedTextBoxId,
    selectedTextBoxFontSize,
    handleSetSelectedTextBoxFontSize,
    handleDeleteSelectedTextBox,
    selectedShadeId,
    selectedShadeColor,
    handleSetSelectedShadeColor,
    handleAddShadeCircle,
    handleAddShadeRect,
    handleDeleteSelectedShade,
    handlePlayerColorChange,
    handlePlayerNumberChange,
    portrait,
}: ActionButtonsProps) {
    const { t } = useTranslation();
    const [playerNumberInput, setPlayerNumberInput] = useState<string>('');
    const isPortrait = portrait ?? false;

    // Paleta ampliada (predefinida): más opciones sin permitir elegir un color arbitrario.
    const EXTENDED_COLOR_OPTIONS: string[] = [
        '#000000', '#FFFFFF', '#9E9E9E', '#616161',
        '#1A237E', '#0D47A1', '#1565C0', '#1976D2', '#1E88E5', '#42A5F5',
        '#004D40', '#00695C', '#00796B', '#00897B', '#26A69A', '#80CBC4',
        '#1B5E20', '#2E7D32', '#388E3C', '#43A047', '#66BB6A', '#A5D6A7',
        '#F57F17', '#F9A825', '#FBC02D', '#FDD835', '#FFEE58',
        '#E65100', '#EF6C00', '#F57C00', '#FB8C00', '#FFA726',
        '#B71C1C', '#C62828', '#D32F2F', '#E53935', '#EF5350',
        '#4A148C', '#6A1B9A', '#7B1FA2', '#8E24AA', '#AB47BC',
        '#263238', '#37474F', '#455A64', '#546E7A', '#607D8B',
    ];

    // Update input field when selectedPlayer changes
    useEffect(() => {
        if (selectedPlayer) {
            // If it's a coach, show "C", if it's a special defender, show the number as-is, if it's a defender, show "x" prefix, otherwise just the number
            const displayValue = selectedPlayer.isCoach
                ? 'C'
                : selectedPlayer.isSpecialDefender
                    ? String(selectedPlayer.number) // Show exactly what's stored (could be "1", "x1", etc.)
                    : selectedPlayer.isDefender
                        ? `x${selectedPlayer.number}`
                        : String(selectedPlayer.number);
            setPlayerNumberInput(displayValue);
        }
    }, [selectedPlayer]);

    const DEFAULT_CONE_COLOR = '#000000';

    const commonButtonStyles = {
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 1.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
        },
    };

    const colorBoxStyles = (selectedColor: string | undefined, currentColor: string) => ({
        backgroundColor: currentColor,
        width: 24,
        height: 24,
        border: `2px solid ${selectedColor === currentColor ? '#000' : 'transparent'}`,
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
        },
    });

    const deleteButtonStyles = {
        ...commonButtonStyles,
        height: 28,
        minWidth: { sm: 80 },
        borderColor: '#ff4444',
        color: '#ff4444',
        fontSize: isPortrait ? '0.65rem' : '0.875rem',
        '&:hover': {
            backgroundColor: '#ff4444',
            color: 'white',
            borderColor: '#ff4444',
        }
    };

    const MoreColorsButton = ({
        selectedColor,
        onSelect,
        ariaLabel,
        tooltip,
    }: {
        selectedColor: string | undefined;
        onSelect: (color: string) => void;
        ariaLabel: string;
        tooltip: string;
    }) => {
        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const open = Boolean(anchorEl);

        // Evitar duplicados: si `colorOptions` ya muestra parte de la paleta, igual podemos mostrarla completa,
        // pero aquí filtramos por seguridad ante arrays repetidos.
        const uniquePalette = Array.from(new Set(EXTENDED_COLOR_OPTIONS));

        return (
            <>
                <Tooltip title={tooltip}>
                    <IconButton
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        aria-label={ariaLabel}
                        size="small"
                        sx={{
                            width: 28,
                            height: 28,
                            border: '2px dashed #9e9e9e',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: '#fafafa',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                            },
                        }}
                    >
                        <AddRoundedIcon sx={{ fontSize: 18, color: '#616161' }} />
                    </IconButton>
                </Tooltip>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            p: 1,
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                        }
                    }}
                >
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 24px)', gap: 1, p: 0.5 }}>
                        {uniquePalette.map((color) => (
                            <Box
                                key={`more-color-${color}`}
                                component="button"
                                onClick={() => {
                                    onSelect(color);
                                    setAnchorEl(null);
                                }}
                                sx={{
                                    ...colorBoxStyles(selectedColor, color),
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    p: 0,
                                }}
                                aria-label={`more-color-${color}`}
                                title={color}
                            />
                        ))}
                    </Box>
                </Popover>
            </>
        );
    };

    return (
        <Box sx={{ mb: 0 }}>
            {/* Text selection controls (font size slider + delete) */}
            {selectedTextBoxId !== null && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t('PlayBuilderText00')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: '#333' }}>
                                {t('PlayBuilderText03')}: {selectedTextBoxFontSize}
                            </Typography>
                            <Slider
                                value={selectedTextBoxFontSize}
                                min={8}
                                max={26}
                                step={1}
                                onChange={(_e, value) => {
                                    const v = Array.isArray(value) ? value[0] : value;
                                    handleSetSelectedTextBoxFontSize(v);
                                }}
                                size="small"
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteSelectedTextBox}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={deleteButtonStyles}
                        >
                            {t('ActionButtons17')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}

            {/* Shade controls */}
            {selectedShadeId !== null && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t('ActionButtons18')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {colorOptions.map((color) => (
                            <Box
                                key={`shade-color-${color.value}`}
                                component="button"
                                onClick={() => handleSetSelectedShadeColor(color.value)}
                                sx={colorBoxStyles(selectedShadeColor, color.value)}
                                aria-label={`shade-color-${color.value}`}
                                title={String(color.value)}
                            />
                        ))}
                        <MoreColorsButton
                            selectedColor={selectedShadeColor}
                            onSelect={handleSetSelectedShadeColor}
                            ariaLabel="shade-more-colors"
                            tooltip="Más colores"
                        />
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteSelectedShade}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={deleteButtonStyles}
                        >
                            {t('ActionButtons17')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}

            {/* Selected Line Controls */}
            {selectedLineId !== null && selectedLine?.type !== 'handoff' && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t(`ActionButtons0${selectedLine?.type === 'dashed' ? 3 : selectedLine?.type === 'zigzag' ? 2 : selectedLine?.type === 'bar' ? 4 : selectedLine?.type === 'shot' ? 5 : 1}`)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {colorOptions.map((color) => (
                            <Box
                                key={color.value}
                                component="button"
                                onClick={() => handleColorChange(color.value)}
                                sx={colorBoxStyles(selectedLine?.color, color.value)}
                            />
                        ))}
                        <MoreColorsButton
                            selectedColor={selectedLine?.color}
                            onSelect={handleColorChange}
                            ariaLabel="line-more-colors"
                            tooltip="Más colores"
                        />
                        <IconButton
                            onClick={toggleShape}
                            sx={{
                                ml: 'auto',
                                width: 28,
                                height: 28,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1.5,
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                }
                            }}
                        >
                            {isDoubleBezier ? <TimelineRoundedIcon style={{ color: 'gray', fontSize: 16 }} /> : <PolylineRoundedIcon style={{ color: 'gray', fontSize: 16 }} />}
                        </IconButton>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteLine}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={deleteButtonStyles}
                        >
                            {t('ActionButtons08')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}
            {/* Handoff Line Controls */}
            {selectedLineId !== null && selectedLine?.type === 'handoff' && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t('ActionButtons16')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {colorOptions.map((color) => (
                            <Box
                                key={color.value}
                                component="button"
                                onClick={() => handleHandoffColorChange(color.value)}
                                sx={colorBoxStyles(selectedLine?.handoffColor, color.value)}
                            />
                        ))}
                        <MoreColorsButton
                            selectedColor={selectedLine?.handoffColor}
                            onSelect={handleHandoffColorChange}
                            ariaLabel="handoff-more-colors"
                            tooltip="Más colores"
                        />
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteLine}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={{ ...deleteButtonStyles, ml: 'auto' }}
                        >
                            {t('ActionButtons17')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}
            {/* Player Color Selection */}
            {selectedPlayerId !== null && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t('ActionButtons13')} {selectedPlayer?.isCoach ? 'C' : selectedPlayer?.isSpecialDefender ? String(selectedPlayer.number) : selectedPlayer?.isDefender ? `x${selectedPlayer.number}` : selectedPlayer?.number}
                    </Typography>

                    {/* Player Number Input, Colors, and Delete in one row */}
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            value={playerNumberInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                // Only allow max 3 characters
                                if (value.length <= 3) {
                                    setPlayerNumberInput(value);
                                }
                            }}
                            inputProps={{
                                maxLength: 3,
                                style: { textAlign: 'center', padding: isPortrait ? '2px 4px' : '6px 8px', fontSize: isPortrait ? '16px' : '13px', color: 'black' }
                            }}
                            sx={{
                                width: isPortrait ? 45 : 50,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    fontWeight: 'bold',
                                    height: isPortrait ? 24 : 'auto',
                                    '& fieldset': {
                                        borderColor: 'lightgrey',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#c0c0c0',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#c0c0c0',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: isPortrait ? '2px 4px' : '6px 8px',
                                }
                            }}
                        />
                        {(() => {
                            // Calculate the current displayed value for the player
                            const currentDisplayValue = selectedPlayer?.isCoach
                                ? 'C'
                                : selectedPlayer?.isSpecialDefender
                                    ? String(selectedPlayer.number || '') // Show exactly what's stored for special defenders
                                    : selectedPlayer?.isDefender
                                        ? `x${selectedPlayer.number}`
                                        : String(selectedPlayer?.number || '');

                            // Check if value has changed and is valid
                            const hasChanged = playerNumberInput !== currentDisplayValue;
                            const isValid = playerNumberInput.length >= 1 && playerNumberInput.length <= 3;
                            const canSave = hasChanged && isValid;

                            return (
                                <IconButton
                                    disabled={!canSave}
                                    onClick={() => {
                                        if (canSave) {
                                            handlePlayerNumberChange(playerNumberInput);
                                        }
                                    }}
                                    sx={{
                                        width: isPortrait ? 24 : 32,
                                        height: isPortrait ? 24 : 32,
                                        bgcolor: canSave ? '#00398f' : 'white',
                                        color: canSave ? 'white' : '#bdbdbd',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1.5,
                                        transition: 'all 0.2s ease-in-out',
                                        boxShadow: canSave ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        '&:hover': {
                                            bgcolor: canSave ? darken('#00398f', 0.1) : 'white',
                                            transform: canSave ? 'translateY(-1px)' : 'none',
                                            boxShadow: canSave ? '0 3px 6px rgba(0,0,0,0.15)' : 'none',
                                        },
                                        '&:disabled': {
                                            bgcolor: 'white',
                                            color: '#bdbdbd',
                                            cursor: 'not-allowed',
                                        }
                                    }}
                                >
                                    <CheckRoundedIcon sx={{ fontSize: isPortrait ? 14 : 18 }} />
                                </IconButton>
                            );
                        })()}

                        <Box sx={{ width: 8 }} />

                        {colorOptions.map((color) => (
                            <Box
                                key={color.value}
                                component="button"
                                onClick={() => handlePlayerColorChange(color.value)}
                                sx={colorBoxStyles(selectedPlayer?.color, color.value)}
                            />
                        ))}
                        <MoreColorsButton
                            selectedColor={selectedPlayer?.color}
                            onSelect={handlePlayerColorChange}
                            ariaLabel="player-more-colors"
                            tooltip="Más colores"
                        />

                        <Box sx={{ width: 8 }} />

                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeletePlayer}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={{ ...deleteButtonStyles, ml: 'auto' }}
                        >
                            {t('ActionButtons17')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}
            {/* Cone Selection */}
            {selectedConeId !== null && (
                <>
                    <Typography sx={{ mb: isPortrait ? 0.5 : 1, mt: isPortrait ? 0.25 : 1, fontWeight: 'bold', fontSize: { xs: '16px', sm: '18px' }, color: 'black', textAlign: 'left' }}>
                        {t('ActionButtons12')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {colorOptions.map((color) => (
                            <Box
                                key={`cone-color-${color.value}`}
                                component="button"
                                onClick={() => handleSetSelectedConeColor(color.value)}
                                sx={colorBoxStyles(selectedConeColor, color.value)}
                                aria-label={`cone-color-${color.value}`}
                                title={String(color.value)}
                            />
                        ))}
                        <MoreColorsButton
                            selectedColor={selectedConeColor}
                            onSelect={handleSetSelectedConeColor}
                            ariaLabel="cone-more-colors"
                            tooltip="Más colores"
                        />
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDeleteCone}
                            startIcon={<DeleteRoundedIcon style={{ fontSize: 16 }} />}
                            sx={{ ...deleteButtonStyles, ml: 'auto' }}
                        >
                            {t('ActionButtons17')}
                        </Button>
                    </Box>
                    <Divider sx={{ my: isPortrait ? 1 : 2 }} />
                </>
            )}
            {/* Action Buttons */}
            <Box sx={{ mb: 1, mt: isPortrait ? 0.5 : 0 }}>
                <Grid container spacing={1}>
                    {[
                        { type: 'straight', label: '01', icon: '/images/playbuilder/straightLight.svg' },
                        { type: 'zigzag', label: '02', icon: '/images/playbuilder/zigzagLight.svg' },
                        { type: 'dashed', label: '03', icon: '/images/playbuilder/dashedLight.svg' },
                        { type: 'bar', label: '04', icon: '/images/playbuilder/screenLight.svg' },
                        { type: 'handoff', label: '16', icon: '/images/playbuilder/handoffLight.svg' },
                    ].map(({ type, label, icon }) => (
                        <Grid key={type} xs={6}>
                            <Button
                                variant="contained"
                                size={isPortrait ? "small" : "medium"}
                                fullWidth
                                onClick={() => createLine(type as any)}
                                sx={{
                                    ...commonButtonStyles,
                                    bgcolor: '#00398f',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: darken('#00398f', 0.1),
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 8px rgba(0,57,143,0.3)',
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: isPortrait ? 11 : 13,
                                }}
                            >
                                <img
                                    src={icon}
                                    alt={`${type} Line`}
                                    style={{
                                        width: isPortrait ? (type === 'handoff' ? 12 : 24) : (type === 'handoff' ? 16 : 32),
                                        marginRight: isPortrait ? 4 : 8,
                                        filter: 'brightness(0) invert(1)',
                                    }}
                                />
                                {t(`ActionButtons${label}`)}
                            </Button>
                        </Grid>
                    ))}
                    <Grid xs={6}>
                        <Button
                            variant="contained"
                            size={isPortrait ? "small" : "medium"}
                            fullWidth
                            onClick={() => createLine('shot')}
                            sx={{
                                ...commonButtonStyles,
                                bgcolor: '#DD620f',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: darken('#DD620f', 0.1),
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(221,98,15,0.3)',
                                },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isPortrait ? 11 : 13,
                            }}
                        >
                            {t('ActionButtons11')}
                        </Button>
                    </Grid>
                    <Grid xs={12}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={handleToggleBall}
                            sx={{
                                ...commonButtonStyles,
                                color: '#DD620f',
                                bgcolor: 'white',
                                borderColor: '#DD620f',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#fff8f3',
                                    borderColor: darken('#DD620f', 0.1),
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(221,98,15,0.2)',
                                },
                                fontSize: isPortrait ? 11 : 13,
                            }}
                            startIcon={<SportsBasketballRoundedIcon style={{ fontSize: isPortrait ? 14 : 18 }} />}
                        >
                            {selectedPlayer?.hasBall ? t('ActionButtons06') : t('ActionButtons05')}
                        </Button>
                    </Grid>
                </Grid>

                <Divider sx={{ my: isPortrait ? 1 : 2 }} />

                {/* Elements Section - Hidden in portrait mode */}
                {!isPortrait && (
                    <>
                        {/* Offensive Players */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {[...Array(6)].map((_, i) => i + 1).map((num) => (
                                <Button
                                    key={num}
                                    variant="outlined"
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('type', 'offensive');
                                        e.dataTransfer.setData('number', num.toString());
                                    }}
                                    onClick={() => handleAddOffensivePlayer(num)}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        minWidth: 0,
                                        p: 0,
                                        borderRadius: 1.5,
                                        bgcolor: 'white',
                                        color: '#00398f',
                                        borderColor: '#e0e0e0',
                                        borderWidth: 1.5,
                                        '&:hover': {
                                            bgcolor: '#f0f4ff',
                                            borderColor: '#00398f',
                                            transform: 'scale(1.05)',
                                        },
                                        fontSize: 14,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {num}
                                </Button>
                            ))}
                            <Button
                                variant="outlined"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('type', 'offensive');
                                    e.dataTransfer.setData('number', '0');
                                }}
                                onClick={() => handleAddOffensivePlayer(0)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    color: '#00398f',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f0f4ff',
                                        borderColor: '#00398f',
                                        transform: 'scale(1.05)',
                                    },
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                }}
                            >
                                C
                            </Button>
                        </Box>

                        {/* Defenders */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {[...Array(6)].map((_, i) => i + 1).map((num) => (
                                <Button
                                    key={num}
                                    variant="outlined"
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('type', 'defender');
                                        e.dataTransfer.setData('number', num.toString());
                                    }}
                                    onClick={() => handleAddDefender(num)}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        minWidth: 0,
                                        p: 0,
                                        borderRadius: 1.5,
                                        bgcolor: 'white',
                                        color: 'red',
                                        borderColor: '#e0e0e0',
                                        borderWidth: 1.5,
                                        '&:hover': {
                                            bgcolor: '#fff0f0',
                                            borderColor: 'red',
                                            transform: 'scale(1.05)',
                                        },
                                        fontSize: 14,
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                    }}
                                >
                                    {`x${num}`}
                                </Button>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {[...Array(6)].map((_, i) => i + 1).map((num) => (
                                <Button
                                    key={`special-${num}`}
                                    variant="outlined"
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('type', 'specialDefender');
                                        e.dataTransfer.setData('number', num.toString());
                                    }}
                                    onClick={() => handleAddSpecialDefender(num)}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        minWidth: 0,
                                        p: 0,
                                        borderRadius: 1.5,
                                        bgcolor: 'white',
                                        borderColor: '#e0e0e0',
                                        borderWidth: 1.5,
                                        position: 'relative',
                                        overflow: 'visible',
                                        '&:hover': {
                                            bgcolor: '#f8f8f8',
                                            borderColor: '#d32f2f',
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    <svg
                                        width="28"
                                        height="28"
                                        viewBox="0 0 28 28"
                                        style={{ display: 'block' }}
                                    >
                                        {/* Círculo rojo con fondo */}
                                        <circle
                                            cx="14"
                                            cy="14"
                                            r="7.5"
                                            fill="#d32f2f"
                                            stroke="#d32f2f"
                                            strokeWidth="1"
                                        />
                                        {/* Brazo izquierdo - semicírculo pequeño */}
                                        <g transform="translate(5.5, 14) rotate(-30)">
                                            <path
                                                d="M -4 1 A 4 1.2 0 0 1 4 1"
                                                fill="none"
                                                stroke="#d32f2f"
                                                strokeWidth="1.1"
                                                strokeLinecap="round"
                                            />
                                        </g>
                                        {/* Brazo derecho - semicírculo pequeño */}
                                        <g transform="translate(22.5, 14) rotate(30)">
                                            <path
                                                d="M -4 1 A 4 1.2 0 0 1 4 1"
                                                fill="none"
                                                stroke="#d32f2f"
                                                strokeWidth="1.1"
                                                strokeLinecap="round"
                                            />
                                        </g>
                                        {/* Número en blanco - centrado en el círculo */}
                                        <text
                                            x="14"
                                            y="14"
                                            textAnchor="middle"
                                            alignmentBaseline="central"
                                            fontSize="10"
                                            fontWeight="bold"
                                            fill="white"
                                            style={{ fontFamily: 'Arial, sans-serif' }}
                                        >
                                            {num}
                                        </text>
                                    </svg>
                                </Button>
                            ))}
                        </Box>

                        {/* Cone, Text, and Shades */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {/* Cone - first in line */}
                            <Button
                                variant="outlined"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('type', 'cone');
                                    e.dataTransfer.setData('color', DEFAULT_CONE_COLOR);
                                }}
                                onClick={() => handleAddCone(DEFAULT_CONE_COLOR)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f8f8f8',
                                        borderColor: '#546e7a',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                                aria-label={t('AddElementsPopup02')}
                                title={t('AddElementsPopup02')}
                            >
                                <ConeIcon size={16} color={DEFAULT_CONE_COLOR} />
                            </Button>
                            {/* Text */}
                            <Button
                                variant="outlined"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('type', 'textBox');
                                }}
                                onClick={handleAddTextBox}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    color: '#333',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f8f8f8',
                                        borderColor: '#546e7a',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                                aria-label={t('PlayBuilderText01')}
                                title={t('PlayBuilderText01')}
                            >
                                <TextFieldsRoundedIcon sx={{ fontSize: 18, color: '#546e7a' }} />
                            </Button>
                            <Button
                                variant="outlined"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('type', 'shadeCircle');
                                }}
                                onClick={handleAddShadeCircle}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f8f8f8',
                                        borderColor: '#546e7a',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                                aria-label="Add shade circle"
                                title="Add shade circle"
                            >
                                <CircleOutlinedIcon sx={{ fontSize: 18, color: '#546e7a' }} />
                            </Button>
                            <Button
                                variant="outlined"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('type', 'shadeRect');
                                }}
                                onClick={handleAddShadeRect}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f8f8f8',
                                        borderColor: '#546e7a',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                                aria-label="Add shade rectangle"
                                title="Add shade rectangle"
                            >
                                <CropSquareRoundedIcon sx={{ fontSize: 18, color: '#546e7a' }} />
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}